import { useEffect, useState, useRef, useMemo } from "react";
import { config } from "./Components/Config";
import { jwtFetch } from "./utils/apiClient";
import { useParams, Link } from "react-router-dom";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import Select, { components } from "react-select";
import CreatableSelect from 'react-select/creatable';
import { format, formatDistanceToNowStrict } from "date-fns";
import { enUS, id as idLocale } from "date-fns/locale";
import ReactQuill from 'react-quill-new';
import { v4 as uuidv4 } from 'uuid'; // Import UUID
import 'react-quill-new/dist/quill.snow.css';
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import DOMPurify from 'dompurify';
import MagicUrl from 'quill-magic-url';

const Quill = ReactQuill.Quill;
Quill.register('modules/magicUrl', MagicUrl);
const BlockEmbed = Quill.import('blots/block/embed');

const icons = Quill.import('ui/icons');
icons['file'] = '<i class="bi bi-paperclip" style="font-size: 14px; font-weight: bold;"></i>';

class HTML5Video extends BlockEmbed {
    static create(value) {
        let node = super.create();
        node.setAttribute('controls', 'true');
        node.setAttribute('width', '100%');
        node.style.maxWidth = '400px';
        node.style.maxHeight = '300px';
        node.setAttribute('src', value);
        return node;
    }

    static value(node) {
        return node.getAttribute('src');
    }
}
HTML5Video.blotName = 'html5video';
HTML5Video.tagName = 'video';
Quill.register(HTML5Video);

export const formatEmailContent = (content) => {
    const API_BASE_URL = config.SUPPORT_ENDPOINT;
    if (!content) return '';

    const contentFixedSpaces = content.replace(/&nbsp;|[\u00A0]/g, ' ');

    let processedContent = contentFixedSpaces.replace(
        /src=(["'])(?:\\|\/)?uploads\//g,
        `src=$1${API_BASE_URL}uploads/`
    );

    const cleanHtml = DOMPurify.sanitize(processedContent, {
        ADD_TAGS: ['img', 'video', 'source', 'iframe', 'br', 'p', 'div', 'span', 'blockquote'],
        ADD_ATTR: ['src', 'alt', 'style', 'width', 'height', 'controls', 'type', 'target', 'rel', 'referrerpolicy', 'class']
    });

    const htmlWithStyle = cleanHtml.replace(
        /<p((?:\s+[^>]*)?)>/gi,
        '<p style="margin-bottom: 1em; min-height: 1.2em;"$1>'
    );

    const htmlWithReferrerPolicy = htmlWithStyle.replace(/<img /g, '<img referrerpolicy="no-referrer" ');

    if (!htmlWithReferrerPolicy.includes('<div') && !htmlWithReferrerPolicy.includes('<p')) {
        const splitRegex = /(On\s+.+\s+wrote:|From:\s+.+\s+Sent:\s+.+)/i;
        const parts = htmlWithReferrerPolicy.split(splitRegex);

        if (parts.length > 1) {

            let formatted = `<div class="email-body">${parts[0]}</div>`;
            const quoteContent = parts.slice(1).join('');
            const cleanQuote = quoteContent.replace(/^>\s?/gm, '');

            formatted += `
             <div class="email-quote mt-4 pl-4 border-l-2 border-gray-300 text-gray-500 text-sm">
                 ${cleanQuote}
             </div>
           `;
            return formatted;
        }

        if (htmlWithReferrerPolicy.includes('\n>')) {
            return htmlWithReferrerPolicy.replace(/\n>\s?(.*)/g, '<blockquote class="pl-4 border-l-2 border-gray-300 text-gray-500 my-2">$1</blockquote>').replace(/\n/g, '<br/>');
        }

        return `<div class="email-content-plaintext">${htmlWithReferrerPolicy}</div>`;
    }

    return `<div class="email-content-html">${htmlWithReferrerPolicy.replace(/<blockquote>/g, '<blockquote class="pl-4 border-l-2 border-gray-300 text-gray-500 my-2">')}</div>`;
};

// [UPDATED] Collapsible Message Component
const CollapsibleMessage = ({ content }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [overflowState, setOverflowState] = useState('hidden');
    const contentRef = useRef(null);
    const MAX_HEIGHT = 200; // Tinggi maksimal sebelum dicollapse

    useEffect(() => {
        if (contentRef.current) {
            setIsOverflowing(contentRef.current.scrollHeight > MAX_HEIGHT);
        }
    }, [content]);

    useEffect(() => {
        if (!isExpanded) {
            setOverflowState('hidden');
        }
    }, [isExpanded]);

    return (
        <div className="position-relative">
            <div
                ref={contentRef}
                className="text-dark message-content"
                style={{
                    maxHeight: isExpanded ? `${contentRef.current?.scrollHeight}px` : `${MAX_HEIGHT}px`,
                    overflow: overflowState,
                    transition: 'max-height 0.6s cubic-bezier(0.25, 1, 0.5, 1)', // Smooth cubic-bezier transition
                    position: 'relative',
                    maskImage: !isExpanded && isOverflowing
                        ? 'linear-gradient(to bottom, black 50%, transparent 100%)'
                        : 'none',
                    WebkitMaskImage: !isExpanded && isOverflowing
                        ? 'linear-gradient(to bottom, black 50%, transparent 100%)'
                        : 'none',
                    wordBreak: 'normal',
                    overflowWrap: 'anywhere'
                }}
                onTransitionEnd={() => {
                    if (isExpanded) setOverflowState('visible');
                }}
                dangerouslySetInnerHTML={{ __html: formatEmailContent(content) }}
            />

            {isOverflowing && (
                <div className={`d-flex ${isExpanded ? 'mt-2' : 'mt-0'}`} style={{ transition: 'margin 0.3s ease' }}>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="btn btn-sm btn-light border rounded px-3 d-flex align-items-center gap-1 bg-white"
                        style={{
                            fontSize: '12px',
                            zIndex: 10,
                            position: 'relative',
                            marginTop: isExpanded ? '0' : '-20px' // Pull button up slightly to overlap fade area
                        }}
                    >
                        {isExpanded ? (
                            <>
                                <i className="bi bi-dash"></i> less
                            </>
                        ) : (
                            <>
                                <i className="bi bi-plus"></i> more
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

const TicketDetail = () => {
    const { id } = useParams();
    const idRef = useRef(id);

    useEffect(() => {
        idRef.current = id;
    }, [id]);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [conversation, setConversation] = useState([]);
    const [firstMessage, setFirstMessage] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const observerTarget = useRef(null);

    // Editor States
    const [editorVisible, setEditorVisible] = useState(null); // 'reply' or 'note' or null
    const [editorContent, setEditorContent] = useState('');
    const [attachments, setAttachments] = useState([]); // [FIX] Attachments State for Chip UI
    const [isSending, setIsSending] = useState(false);
    const [replyError, setReplyError] = useState("");
    const quillRef = useRef(null);
    const [conversationId, setConversationId] = useState(null); // State for conversationId
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showReopenModal, setShowReopenModal] = useState(false);
    const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);

    // Reply Header States
    const [showCC, setShowCC] = useState(false);
    const [ccValues, setCcValues] = useState([]);
    const [ccInputValue, setCcInputValue] = useState("");

    const crmUser = JSON.parse(localStorage.getItem('crmUser'));

    const handleCcKeyDown = (event) => {
        if (!ccInputValue) return;
        switch (event.key) {
            case 'Enter':
            case 'Tab':
            case ',':
                // Check if input contains commas (handling paste or multiple emails)
                if (ccInputValue.includes(',')) {
                    const emails = ccInputValue.split(',').map(e => e.trim()).filter(e => e);
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const newOptions = [];

                    emails.forEach(email => {
                        if (emailRegex.test(email)) {
                            // Avoid duplicates
                            const exists = ccValues.some(c => c.value === email || c.email === email);
                            if (!exists) {
                                newOptions.push({ label: email, value: email, email: email, __isNew__: true });
                            }
                        }
                    });

                    if (newOptions.length > 0) {
                        event.preventDefault();
                        setCcValues(prev => [...prev, ...newOptions]);
                        setCcInputValue("");
                    }
                } else {
                    const val = ccInputValue.replace(',', '').trim();
                    // Simple email validation
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    if (val && emailRegex.test(val)) {
                        event.preventDefault();

                        // Avoid duplicates
                        const exists = ccValues.some(c => c.value === val || c.email === val);
                        if (!exists) {
                            const newOption = { label: val, value: val, email: val, __isNew__: true };
                            setCcValues(prev => [...prev, newOption]);
                        }
                        setCcInputValue("");
                    }
                }
                break;
        }
    };

    // [NEW] Handle Input Change to detect paste with commas immediately
    const handleCcInputChange = (newValue, actionMeta) => {
        // If the user pastes text containing commas
        if (newValue.includes(',')) {
            const emails = newValue.split(',').map(e => e.trim()).filter(e => e);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const newOptions = [];

            emails.forEach(email => {
                if (emailRegex.test(email)) {
                    const exists = ccValues.some(c => c.value === email || c.email === email);
                    const inNew = newOptions.some(c => c.value === email);
                    if (!exists && !inNew) {
                        newOptions.push({ label: email, value: email, email: email, __isNew__: true });
                    }
                }
            });

            if (newOptions.length > 0) {
                setCcValues(prev => [...prev, ...newOptions]);
                setCcInputValue("");
                return;
            }
        }
        setCcInputValue(newValue);
    };

    // Image Preview State
    const [previewImage, setPreviewImage] = useState(null);
    const [previewVideo, setPreviewVideo] = useState(null); // Video Preview State
    const [previewPdf, setPreviewPdf] = useState(null); // PDF Preview State
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const isImage = (fileName) => {
        return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName);
    };

    const isVideo = (fileName) => {
        return /\.(mp4|webm|ogg|mov)$/i.test(fileName);
    };

    const isPdf = (fileName) => {
        return /\.(pdf)$/i.test(fileName);
    };

    const handleAttachmentClick = (att) => {
        const url = `${config.SUPPORT_ENDPOINT}uploads/${att.fileUrl}`;
        if (isImage(att.fileName)) {
            setPreviewImage(url);
            setPreviewVideo(null);
            setPreviewPdf(null);
            setShowPreviewModal(true);
        } else if (isVideo(att.fileName)) {
            setPreviewVideo(url);
            setPreviewImage(null);
            setPreviewPdf(null);
            setShowPreviewModal(true);
        } else if (isPdf(att.fileName)) {
            setPreviewPdf(url);
            setPreviewImage(null);
            setPreviewVideo(null);
            setShowPreviewModal(true);
        } else {
            // Direct download/open for others
            window.open(url, '_blank');
        }
    };

    // Dropdown Options
    const [queueOptions, setQueueOptions] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [ccOptions, setCcOptions] = useState([]); // Options for CC dropdown

    // Form States (for the sidebar)
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [selectedQueue, setSelectedQueue] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedResolution, setSelectedResolution] = useState(null);
    const [selectedType, setSelectedType] = useState(null);

    // Sender Edit State
    const [isEditingSender, setIsEditingSender] = useState(false);
    const [senderName, setSenderName] = useState("");
    const [senderEmail, setSenderEmail] = useState("");
    const [senderError, setSenderError] = useState("");

    const handleEditSenderClick = () => {
        setSenderName(data?.receiveName || "");
        setSenderEmail(data?.receiveEmail || "");
        setSenderError("");
        setIsEditingSender(true);
    };

    const handleCancelEditSender = () => {
        setIsEditingSender(false);
        setSenderName("");
        setSenderEmail("");
        setSenderError("");
    };

    const handleSenderSelect = (opt) => {
        setSenderError("");
        if (opt) {
            if (opt.__isNew__) {
                setSenderEmail(opt.value);
            } else {
                setSenderName(opt.label);
                setSenderEmail(opt.email || opt.value || "");
            }
        } else {
            setSenderEmail("");
        }
    };

    const handleSaveSender = async () => {
        setSenderError("");
        if (!senderName?.trim() || !senderEmail?.trim()) {
            setSenderError("Name and Email are required");
            return;
        }
        await updateTicketFields({
            receiveName: senderName,
            receiveEmail: senderEmail
        });
        setIsEditingSender(false);
        setData(prev => ({ ...prev, receiveName: senderName, receiveEmail: senderEmail }));
    };

    // Hardcoded Options (Mocking if API doesn't provide them yet)
    const statusOptions = [
        { label: 'New', value: 'NEW' },
        { label: 'In Progress', value: 'IN_PROGRESS' },
        { label: 'On Hold', value: 'ON_HOLD' },
        { label: 'Information Requested', value: 'INFORMATION_REQUESTED' },
        { label: 'Solved', value: 'SOLVED' },
        { label: 'Closed', value: 'CLOSED' }
    ];

    const priorityOptions = [
        { label: 'Emergency', value: 'EMERGENCY' },
        { label: 'Urgent', value: 'URGENT' },
        { label: 'Normal', value: 'NORMAL' },
        { label: 'Low', value: 'LOW' },
    ];

    const resolutionOptions = [
        { label: 'No Resolution', value: 'NO_RESOLUTION' },
        { label: 'Call', value: 'CALL' },
        { label: 'Chat', value: 'CHAT' },
        { label: 'Email', value: 'EMAIL' },
        { label: 'Internal', value: 'INTERNAL' },
        { label: 'Resolved', value: 'RESOLVED' },
        { label: 'User Resolved', value: 'USER_RESOLVED' },
        { label: 'WhatsApp', value: 'WA' }
    ];

    const typeOptions = [
        { label: 'No Type', value: 'NO_TYPE' },
        { label: 'Incident', value: 'INCIDENT' },
        { label: 'P1', value: 'P1' },
        { label: 'P2', value: 'P2' },
        { label: 'P3', value: 'P3' },
        { label: 'P4', value: 'P4' },
        { label: 'Product Issue', value: 'PRODUCT_ISSUE' },
        { label: 'Purchase Request', value: 'PURCHASE_REQUEST' },
        { label: 'Query', value: 'QUERY' },
        { label: 'Usage Issue', value: 'USAGE_ISSUE' }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'NEW': return '#1b8756'; // Warning/Yellow
            case 'IN_PROGRESS': return '#0d6efd'; // Primary/Blue
            case 'ON_HOLD': return '#6c757d'; // Orange
            case 'SOLVED': return '#93ace6'; // Success/Green
            case 'CLOSED': return '#8410DCFF'; // Secondary/Grey
            case 'INFORMATION_REQUESTED': return '#ffc108'; // Info/Blue
            default: return '#6c757d';
        }
    };
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'EMERGENCY': return '#dc3545'; // Danger/Red
            case 'URGENT': return '#fd7e14'; // Orange
            case 'NORMAL': return '#0d6efd'; // Blue
            case 'LOW': return '#6c757d'; // Grey
            default: return '#0d6efd';
        }
    };

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        setConversation([]);
        fetchTicketData();
        fetchConversation(1);
        fetchMetadata();
    }, [id]);

    const fetchTicketData = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${config.SUPPORT_ENDPOINT}tickets/${id}`);
            const json = await response.json();
            if (json.success && json.data) {
                const ticket = json.data;
                setData(ticket);
                setSelectedStatus(statusOptions.find(opt => opt.value === ticket.status) || { value: ticket.status, label: ticket.status });
                setSelectedPriority(priorityOptions.find(opt => opt.value === ticket.priority) || { value: ticket.priority, label: ticket.priority });
                setSelectedQueue(queueOptions.find(opt => opt.value === ticket.queueId) || { value: ticket.queueId, label: ticket.queue?.name || ticket.queueId });
                setSelectedResolution(resolutionOptions.find(opt => opt.value === ticket.resolution) || { value: ticket.resolution, label: ticket.resolution });
                setSelectedType(typeOptions.find(opt => opt.value === ticket.type) || { value: ticket.type, label: ticket.type });
                // Map ticketTags to select options structure
                if (ticket.ticketTags && Array.isArray(ticket.ticketTags)) {
                    const mappedTags = ticket.ticketTags.map(tt => ({
                        value: tt.tagId,
                        label: tt.tag.name,
                        color: tt.tag.color
                    }));
                    setSelectedTags(mappedTags);
                }
            }
        } catch (error) {
            console.error("Error fetching ticket:", error);
        }
        setIsLoading(false);
    };


    const fetchConversation = async (pageNum = 1) => {
        try {
            if (pageNum > 1) setIsFetchingMore(true);

            const response = await fetch(`${config.SUPPORT_ENDPOINT}conversations?ticketId=${id}&page=${pageNum}&limit=10`);
            const json = await response.json();
            if (json.data) {
                if (pageNum === 1) {
                    setConversation(json.data);
                    if (json.firstMessage) {
                        setFirstMessage(json.firstMessage);
                    }
                } else {
                    setConversation(prev => [...prev, ...json.data]);
                }

                if (json.meta) {
                    setHasMore(json.meta.hasNextPage);
                }
            }
        } catch (error) {
            console.error("Error fetching conversation:", error);
        } finally {
            if (pageNum > 1) setIsFetchingMore(false);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !isFetchingMore && !isLoading) {
                    setPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [hasMore, isFetchingMore, isLoading]);

    useEffect(() => {
        if (page > 1) {
            fetchConversation(page);
        }
    }, [page]);

    const fetchMetadata = async () => {
        try {
            // Fetch Queues
            const queuesRes = await fetch(`${config.SUPPORT_ENDPOINT}queues?limit=false`);
            const queuesJson = await queuesRes.json();
            if (queuesJson.success) {
                setQueueOptions(queuesJson.data.map(q => ({ value: q.id, label: q.name })));
            }

            // Fetch Tags
            const tagsRes = await fetch(`${config.SUPPORT_ENDPOINT}tags?limit=false`);
            const tagsJson = await tagsRes.json();
            if (tagsJson.success) {
                setTagOptions(tagsJson.data.map(t => ({ value: t.id, label: t.name, color: t.color })));
            }

            const agentsRes = await jwtFetch(`${config.ENDPOINT}get/user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_department: 'Support', limit: 1000 })
            });
            const agentsJson = await agentsRes.json();
            if (agentsJson.status === 200) {
                const agents = agentsJson.data.map(u => ({ value: u.user_keyid, label: u.full_name, email: u.email_address, avatar: u.profile_picture }));
                const optionsWithUnassigned = [{ value: '', label: 'Unassigned', email: null }, ...agents];
                setAgentOptions(optionsWithUnassigned);
                setCcOptions(agents);
            }

        } catch (error) {
            console.error("Error fetching metadata:", error);
        }
    };

    // Update form values when data or options change
    useEffect(() => {
        if (data) {
            if (queueOptions.length > 0 && data.queue_id) {
                setSelectedQueue(queueOptions.find(q => q.value === data.queue_id));
            }
            if (agentOptions.length > 0 && data.userEmail) {
                setSelectedAgent(agentOptions.find(a => a.email === data.userEmail));
            }
            // Logic for tags if they are in data
        }
    }, [data, queueOptions, agentOptions]);

    // Custom Styles for React Select
    const customStyles = {
        control: (base) => ({
            ...base,
            minHeight: '38px',
            fontSize: '14px'
        }),
        menu: (base) => ({
            ...base,
            fontSize: '14px',
            zIndex: 9999
        }),
        multiValue: (styles, { data }) => {
            const color = data.color || '#e0e0e0';
            return {
                ...styles,
                borderRadius: "8px",
                backgroundColor: color,
            };
        },
        multiValueLabel: (styles, { data }) => ({
            ...styles,
            color: data.color ? 'white' : 'black',
        }),
        multiValueRemove: (styles, { data }) => ({
            ...styles,
            color: data.color ? 'white' : 'black',
            ':hover': {
                backgroundColor: data.color ? data.color : '#d0d0d0',
                color: 'white',
            },
        }),
    };

    // [REFACTORED] Core Upload Function - Returns Promise resolving to { url, fileName, type }
    const uploadFileToServer = async (file) => {
        if (!file) return null;

        const formData = new FormData();
        formData.append('file', file);
        if (idRef.current) {
            formData.append('ticketId', idRef.current);
        }

        try {
            const response = await fetch(`${config.SUPPORT_ENDPOINT}upload`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }
            const data = await response.json();
            const storedFileName = data?.url ? data.url.split('/').filter(Boolean).pop() : data.fileName;
            const fileUrl = config.SUPPORT_BASEURL + data.url || (data.fileName ? `${config.SUPPORT_ENDPOINT}uploads/${data.fileName}` : null);

            if (fileUrl && storedFileName) {
                return {
                    name: file.name,
                    fileName: storedFileName,
                    url: fileUrl,
                    type: file.type || 'application/octet-stream'
                };
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            alert(`Error uploading ${file.name}`);
        }
        return null;
    };

    // [REFACTORED] Insert Image to Editor
    const insertImageToEditor = (fileData) => {
        if (!fileData || !quillRef.current) return;
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true) || { index: quill.getLength() };

        quill.insertEmbed(range.index, 'image', fileData.url);
        quill.setSelection(range.index + 1); // Move cursor after image

        setAttachments(prev => [...prev, fileData]);
    };

    const uploadImage = async (file) => {
        const fileData = await uploadFileToServer(file);
        if (fileData) {
            insertImageToEditor(fileData);
        }
    };

    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.setAttribute('multiple', 'true');
        input.click();

        input.onchange = async () => {
            const files = Array.from(input.files);
            // Upload sequentially to maintain order
            for (const file of files) {
                await uploadImage(file);
            }
        };
    };

    // [FIX] Handle Paste, Drop, and Base64 Images
    useEffect(() => {
        if (!editorVisible) return;

        let intervalId;
        let observer;

        const handleBase64Image = async (imgNode) => {
            // Avoid double processing
            if (imgNode.dataset.processing) return;
            imgNode.dataset.processing = "true";

            // Show loading state
            imgNode.style.opacity = '0.5';
            imgNode.style.border = '2px solid orange';

            try {
                // Convert base64 to File
                const res = await jwtFetch(imgNode.src);
                const blob = await res.blob();
                const file = new File([blob], "pasted-image.png", { type: blob.type });

                // Upload
                const formData = new FormData();
                formData.append('file', file);
                if (idRef.current) {
                    formData.append('ticketId', idRef.current);
                }

                const response = await fetch(`${config.SUPPORT_ENDPOINT}upload`, {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    const data = await response.json();
                    const storedFileName = data?.url ? data.url.split('/').filter(Boolean).pop() : data.fileName;
                    const imageUrl = config.SUPPORT_BASEURL + data.url || (data.fileName ? `${config.SUPPORT_ENDPOINT}uploads/${data.fileName}` : null);

                    if (imageUrl && storedFileName) {
                        // [CRITICAL FIX] Update Quill's internal model to reflect the change
                        // This prevents infinite loops where React re-renders with old Base64 content
                        if (quillRef.current) {
                            const quill = quillRef.current.getEditor();
                            const blot = Quill.find(imgNode);
                            if (blot) {
                                const index = quill.getIndex(blot);
                                quill.deleteText(index, 1);
                                quill.insertEmbed(index, 'image', imageUrl);
                                // Move cursor after image
                                quill.setSelection(index + 1);
                            } else {
                                // Fallback if blot not found (rare)
                                imgNode.src = imageUrl;
                                imgNode.style.opacity = '1';
                                imgNode.style.border = 'none';
                            }
                        } else {
                            imgNode.src = imageUrl;
                            imgNode.style.opacity = '1';
                            imgNode.style.border = 'none';
                        }

                        // Add to attachments state
                        setAttachments(prev => [...prev, {
                            name: file.name,
                            fileName: storedFileName,
                            url: imageUrl,
                            type: file.type
                        }]);
                    }
                } else {
                    console.error("Upload failed");
                    imgNode.style.border = '2px solid red';
                }
            } catch (err) {
                console.error("Failed to upload base64 image", err);
                imgNode.style.border = '2px solid red';
            } finally {
                delete imgNode.dataset.processing;
            }
        };

        const attachListeners = () => {
            if (quillRef.current) {
                const quill = quillRef.current.getEditor();

                // 1. Attach Event Listeners (Capture Phase)
                // We use arrow functions wrapper to ensure we can remove them later if needed, 
                // but since they are defined in scope, we can just pass them.
                quill.root.addEventListener('paste', handlePaste, true);
                quill.root.addEventListener('drop', handleDrop, true);
                quill.root.addEventListener('dragover', handleDragOver, true);

                // 2. Attach MutationObserver as Fallback for Base64 Images
                observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'childList') {
                            mutation.addedNodes.forEach((node) => {
                                if (node.nodeName === 'IMG' && node.src && node.src.startsWith('data:')) {
                                    handleBase64Image(node);
                                } else if (node.querySelectorAll) {
                                    const images = node.querySelectorAll('img[src^="data:"]');
                                    images.forEach(img => handleBase64Image(img));
                                }
                            });
                        } else if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
                            if (mutation.target.nodeName === 'IMG' && mutation.target.src && mutation.target.src.startsWith('data:')) {
                                handleBase64Image(mutation.target);
                            }
                        }
                    });
                });

                observer.observe(quill.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });

                clearInterval(intervalId);
            }
        };

        const handlePaste = async (e) => {
            const clipboardData = e.clipboardData || window.clipboardData;
            const items = clipboardData.items;
            const filesToUpload = [];

            // Prevent default paste if we find images
            let hasImage = false;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    hasImage = true;
                    const file = items[i].getAsFile();
                    if (file) filesToUpload.push(file);
                }
            }

            if (hasImage) {
                e.preventDefault();
                e.stopPropagation(); // Stop bubbling

                // Upload Sequentially
                for (const file of filesToUpload) {
                    await uploadImage(file);
                }
            }
        };

        const handleDrop = async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const filesToUpload = [];
            const items = e.dataTransfer.items;

            // Priority: Check items (DataTransferItemList)
            if (items) {
                for (let i = 0; i < items.length; i++) {
                    if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
                        const file = items[i].getAsFile();
                        if (file) filesToUpload.push(file);
                    }
                }
            }

            // Fallback: Check files (FileList) if items didn't yield results or unavailable
            if (filesToUpload.length === 0 && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                for (let i = 0; i < e.dataTransfer.files.length; i++) {
                    const file = e.dataTransfer.files[i];
                    if (file.type.startsWith('image/')) {
                        filesToUpload.push(file);
                    }
                }
            }

            // Upload Sequentially
            for (const file of filesToUpload) {
                await uploadImage(file);
            }
        };

        const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
        };

        // Try immediately
        attachListeners();

        // If not found, poll
        if (!quillRef.current) {
            intervalId = setInterval(attachListeners, 200);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (observer) observer.disconnect();
            if (quillRef.current) {
                try {
                    const quill = quillRef.current.getEditor();
                    quill.root.removeEventListener('paste', handlePaste, true);
                    quill.root.removeEventListener('drop', handleDrop, true);
                    quill.root.removeEventListener('dragover', handleDragOver, true);
                } catch (e) {
                    // Ignore error if quill instance is gone
                }
            }
        };
    }, [editorVisible]);

    const videoHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'video/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('file', file);
            if (id) {
                formData.append('ticketId', id);
            }

            try {
                const response = await fetch(`${config.SUPPORT_ENDPOINT}upload`, {
                    method: 'POST',
                    body: formData
                });

                if (!response.ok) {
                    throw new Error('Upload failed');
                }
                const data = await response.json();
                const storedFileName = data?.url ? data.url.split('/').filter(Boolean).pop() : data.fileName;
                const videoUrl = config.SUPPORT_BASEURL + data.url || (data.fileName ? `${config.SUPPORT_ENDPOINT}uploads/${data.fileName}` : null);

                if (videoUrl && storedFileName) {
                    const quill = quillRef.current.getEditor();
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'html5video', videoUrl);
                    setAttachments(prev => [...prev, {
                        name: file.name,
                        fileName: storedFileName,
                        url: videoUrl,
                        type: file.type || 'application/octet-stream'
                    }]);
                } else {
                    alert("Failed to upload video: No URL returned");
                }
            } catch (error) {
                console.error("Error uploading video:", error);
                alert("Error uploading video");
            }
        };
    };

    const fileHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', '.pdf, .csv, .doc, .docx, .xls, .xlsx, .txt');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('file', file);
            if (id) {
                formData.append('ticketId', id);
            }

            try {
                const response = await fetch(`${config.SUPPORT_ENDPOINT}upload`, {
                    method: 'POST',
                    body: formData
                });

                if (!response.ok) {
                    throw new Error('Upload failed');
                }
                const data = await response.json();
                const storedFileName = data?.url ? data.url.split('/').filter(Boolean).pop() : data.fileName;
                const fileUrl = config.SUPPORT_BASEURL + data.url || (data.fileName ? `${config.SUPPORT_ENDPOINT}uploads/${data.fileName}` : null);

                if (fileUrl && storedFileName) {
                    // [FIX] Add to attachments state instead of inserting into editor
                    setAttachments(prev => [...prev, {
                        name: file.name,
                        fileName: storedFileName,
                        url: fileUrl,
                        type: file.type || 'application/octet-stream'
                    }]);
                } else {
                    alert("Failed to upload file: No URL returned");
                }
            } catch (error) {
                console.error("Error uploading file:", error);
                alert("Error uploading file");
            }
        };
    };

    const removeAttachment = (index) => {
        const attachmentToRemove = attachments[index];
        setAttachments(prev => prev.filter((_, i) => i !== index));

        if (quillRef.current && attachmentToRemove) {
            const quill = quillRef.current.getEditor();
            const delta = quill.getContents();
            let currentPos = 0;

            for (const op of delta.ops) {
                if (op.insert) {
                    const isObject = typeof op.insert === 'object';
                    const length = isObject ? 1 : op.insert.length;

                    if (isObject) {
                        if ((op.insert.image === attachmentToRemove.url) || (op.insert.html5video === attachmentToRemove.url)) {
                            quill.deleteText(currentPos, 1);
                            return;
                        }
                    }
                    currentPos += length;
                }
            }
        }
    };

    const modules = useMemo(() => ({
        magicUrl: true,
        keyboard: {
            bindings: {
                'list autofill': {
                    key: ' ',
                    collapsed: true,
                    format: { list: false },
                    prefix: /^\s*(\d+)\.$/,
                    handler: function (range, context) {
                        const match = context.prefix.match(/^\s*(\d+)\.$/);
                        if (match) {
                            this.quill.insertText(range.index, ' ');
                            return false;
                        }
                        return true;
                    }
                }
            }
        },
        uploader: {
            handler: (range, files) => {
                const filesArray = Array.from(files);
                filesArray.forEach(file => {
                    if (file.type.startsWith('image/')) {
                        uploadImage(file);
                    } else if (file.type.startsWith('video/')) {

                    }
                });
            }
        },
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                ['image', 'video', 'file', 'link'],
            ],
            handlers: {
                image: imageHandler,
                video: videoHandler,
                file: fileHandler
            }
        }
    }), []);

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image', 'video', 'html5video'
    ];

    const handleReplyClick = () => {
        setEditorVisible('reply');
        setEditorContent('');
        setCcInputValue("");
        setConversationId(uuidv4());

        const sourceConversations = (conversation && conversation.length > 0)
            ? conversation
            : (data && data.conversations ? data.conversations : []);

        if (sourceConversations.length > 0) {
            const nonStaffConversations = sourceConversations
                .filter(c => !c.staff)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            let targetConversation = null;

            if (nonStaffConversations.length > 0) {
                targetConversation = nonStaffConversations[0];
            } else if (firstMessage) {
                targetConversation = firstMessage;
            }

            if (targetConversation && targetConversation.conversationCcs && targetConversation.conversationCcs.length > 0) {
                const uniqueCcs = [];
                const seen = new Set();

                targetConversation.conversationCcs.forEach(cc => {
                    if (cc.email && !seen.has(cc.email)) {
                        seen.add(cc.email);
                        uniqueCcs.push(cc);
                    }
                });

                if (uniqueCcs.length > 0) {
                    const newCcValues = uniqueCcs.map(cc => {
                        const existingOption = ccOptions.find(opt => opt.email === cc.email || opt.value === cc.email);
                        if (existingOption) return existingOption;
                        return {
                            label: cc.email,
                            value: cc.email,
                            email: cc.email
                        };
                    });
                    setCcValues(newCcValues);
                    setShowCC(true);
                } else {
                    setCcValues([]);
                    setShowCC(false);
                }
            } else {
                // If no CCs in the target conversation OR no target conversation found
                setCcValues([]);
                setShowCC(false);
            }
        } else if (firstMessage && firstMessage.conversationCcs && firstMessage.conversationCcs.length > 0) {
            // [MODIFIED] Fallback to firstMessage if no conversation history exists yet
            const uniqueCcs = [];
            const seen = new Set();

            firstMessage.conversationCcs.forEach(cc => {
                if (cc.email && !seen.has(cc.email)) {
                    seen.add(cc.email);
                    uniqueCcs.push(cc);
                }
            });

            if (uniqueCcs.length > 0) {
                const newCcValues = uniqueCcs.map(cc => {
                    const existingOption = ccOptions.find(opt => opt.email === cc.email || opt.value === cc.email);
                    if (existingOption) return existingOption;
                    return {
                        label: cc.email,
                        value: cc.email,
                        email: cc.email
                    };
                });
                setCcValues(newCcValues);
                setShowCC(true);
            }
        }
    };

    const handleNoteClick = () => {
        setEditorVisible('note');
        setEditorContent('');
        setConversationId(uuidv4()); // Generate new UUID
        setCcValues([]);
        setCcInputValue(""); // Clear CC Input
        setShowCC(false);
    };

    const handleCloseClick = () => {
        setShowCloseModal(true);
    };

    const handleReopenClick = () => {
        setShowReopenModal(true);
    };

    const handleToggleWatch = async () => {
        const nextValue = !data?.isWatch;
        await updateTicketField('isWatch', nextValue);
        setData(prev => (prev ? { ...prev, isWatch: nextValue } : prev));
    };

    const confirmReopen = async () => {
        try {
            const payload = { status: 'IN_PROGRESS' };
            const response = await fetch(`${config.SUPPORT_ENDPOINT}tickets/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const json = await response.json();
            if (json.success) {
                setShowReopenModal(false);
                fetchTicketData();
            } else {
                alert("Failed to reopen ticket.");
            }
        } catch (error) {
            console.error("Error reopening ticket:", error);
            alert("Error reopening ticket.");
        }
    };

    const handleCancelEditor = () => {
        setEditorVisible(null);
        setEditorContent('');
        setShowCC(false);
        setCcValues([]);
        setCcInputValue(""); // Clear CC Input
        setAttachments([]); // [FIX] Clear attachments
        setConversationId(null); // Clear UUID
    };

    const updateTicketFields = async (updates) => {
        try {
            const response = await fetch(`${config.SUPPORT_ENDPOINT}tickets/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updates)
            });
            const json = await response.json();
            if (!json.success) return alert(`Failed to update ticket`);
            if (json.success) return fetchTicketData();
        } catch (error) {
            console.error(`Error updating ticket:`, error);
            alert(`Error updating ticket`);
        }
    };

    const updateTicketField = async (field, value) => {
        return updateTicketFields({ [field]: value });
    };



    const handlePreSubmit = () => {
        // [FIX] Validate RAW content length, but do not strip tags for the payload
        // This ensures we don't accidentally send empty string if it's just an image or if user typed spaces
        const rawText = editorContent.replace(/<[^>]*>/g, '').trim();
        if (!rawText && attachments.length === 0 && !editorContent.includes('<img') && !editorContent.includes('<video')) {
            setReplyError("Message cannot be empty");
            return;
        }
        setReplyError("");
        setShowSubmitConfirmModal(true);
    };

    const confirmSubmit = async () => {
        setIsSending(true);
        const crmUser = JSON.parse(localStorage.getItem('crmUser'));

        const finalContent = editorContent;

        const payload = {
            agentEmail: crmUser?.email_address || "user@point-star.com",
            agentName: crmUser?.full_name || "Current User",
            staff: editorVisible === 'note', // true if note, false if reply
            text: finalContent,
            ticketId: id,
            cc: ccValues.map(c => c.email || c.value), // Add CCs to payload
            attachments: attachments.map(att => att.fileName), // [ADDED] Send attachment filenames
            // conversationId: conversationId // Add conversationId to payload
        };

        try {
            const response = await fetch(`${config.SUPPORT_ENDPOINT}conversations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                // Refresh conversations
                setPage(1);
                fetchConversation(1);

                // Clear and close editor
                setEditorVisible(null);
                setEditorContent('');
                setShowCC(false);
                setCcValues([]);
                setAttachments([]); // [FIX] Clear attachments
                setConversationId(null);
                setShowSubmitConfirmModal(false);
            } else {
                alert("Failed to send message");
            }
        } catch (error) {
            console.error("Error sending message:", error);
            alert("Error sending message");
        }
        setIsSending(false);
    };

    const handleConfirmClose = async () => {
        updateTicketField('status', 'CLOSED')
        setShowCloseModal(false);
    };

    // Determine locale at component level (or hook)
    const userLocale = navigator.language || 'en-US';
    const currentLocale = userLocale.startsWith('id') ? idLocale : enUS;

    // Helper to get time ago
    const timeAgo = (date) => {
        if (!date) return '';
        return formatDistanceToNowStrict(new Date(date), { addSuffix: true, locale: currentLocale });
    };

    // Custom Option with Badge/Icon Logic
    const StatusOption = (props) => {

        const getStatusInitial = (status) => {
            return status ? status.charAt(0).toUpperCase() : '?';
        };

        return (
            <components.Option {...props}>
                <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2 text-white fw-bold"
                        style={{ width: '24px', height: '24px', backgroundColor: getStatusColor(props.data.value), fontSize: '12px' }}>
                        {getStatusInitial(props.data.value)}
                    </div>
                    <span>{props.data.label}</span>
                </div>
            </components.Option>
        );
    };

    const StatusSingleValue = (props) => {
        const getStatusInitial = (status) => {
            return status ? status.charAt(0).toUpperCase() : '?';
        };

        return (
            <components.SingleValue {...props}>
                <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2 text-white fw-bold"
                        style={{ width: '24px', height: '24px', backgroundColor: getStatusColor(props.data.value), fontSize: '12px' }}>
                        {getStatusInitial(props.data.value)}
                    </div>
                    <span>{props.data.label}</span>
                </div>
            </components.SingleValue>
        );
    };

    const PriorityOption = (props) => {

        const getPriorityInitial = (priority) => {
            return priority ? priority.charAt(0).toUpperCase() : 'N';
        };

        return (
            <components.Option {...props}>
                <div className="d-flex align-items-center">
                    <div className="rounded d-flex align-items-center justify-content-center me-2 text-white fw-bold"
                        style={{ width: '24px', height: '24px', backgroundColor: getPriorityColor(props.data.value), fontSize: '12px', borderRadius: '4px' }}>
                        {getPriorityInitial(props.data.value)}
                    </div>
                    <span>{props.data.label}</span>
                </div>
            </components.Option>
        );
    };

    const PrioritySingleValue = (props) => {
        const getPriorityInitial = (priority) => {
            return priority ? priority.charAt(0).toUpperCase() : 'N';
        };

        return (
            <components.SingleValue {...props}>
                <div className="d-flex align-items-center">
                    <div className="rounded d-flex align-items-center justify-content-center me-2 text-white fw-bold"
                        style={{ width: '24px', height: '24px', backgroundColor: getPriorityColor(props.data.value), fontSize: '12px', borderRadius: '4px' }}>
                        {getPriorityInitial(props.data.value)}
                    </div>
                    <span>{props.data.label}</span>
                </div>
            </components.SingleValue>
        );
    };

    const CcMultiValue = (props) => {
        const { label, email, value } = props.data;
        const displayLabel = (label && label !== email && label !== value) ? label : (email || value);

        return (
            <components.MultiValue {...props}>
                <div className="d-flex align-items-center">
                    {props.data.avatar && (
                        <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-1" style={{ width: '16px', height: '16px', overflow: 'hidden' }}>
                            <img src={props.data.avatar} alt="" style={{ width: '100%', height: '100%' }} />
                        </div>
                    )}
                    <span>{displayLabel}</span>
                    {(label && label !== email && label !== value && email) && <span className="text-muted ms-1 small"> - {email}</span>}
                </div>
            </components.MultiValue>
        );
    };

    const CcOption = (props) => {
        return (
            <components.Option {...props}>
                <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', overflow: 'hidden' }}>
                        {props.data.avatar ? <img src={props.data.avatar} alt="" style={{ width: '100%', height: '100%' }} /> : (props.data.label ? props.data.label.charAt(0).toUpperCase() : 'U')}
                    </div>
                    <div>
                        <div className="fw-bold text-dark" style={{ fontSize: '14px' }}>{props.data.label}</div>
                        <div className="text-muted small" style={{ fontSize: '12px' }}>{props.data.email || props.data.value}</div>
                    </div>
                </div>
            </components.Option>
        );
    };

    const history = useMemo(() => {
        if (!conversation || conversation.length === 0) return [];
        if (firstMessage && conversation[conversation.length - 1].id === firstMessage.id) {
            return conversation.slice(0, conversation.length - 1);
        }
        return conversation;
    }, [conversation, firstMessage]);

    return (
        <div id="support-ticket">
            <div className="container-fluid p-0">
                <div className="row g-0 mh-100vh">
                    <div className="col-md-2 border-end layout-sticky-sidebar">
                        <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
                            <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
                        </div>
                        <hr className="m-0 d-none d-md-block" />
                        <Menu active="support" />
                    </div>
                    <div className="col-md-10 layout-scroll-column">
                        <div className="layout-sticky-header">
                            <Header />
                            <hr className="m-0" />
                        </div>
                        <div className="main bg-light">
                            {isLoading ? (
                                <div className="p-4">Loading...</div>
                            ) : data ? (
                                <div className="container-fluid p-4">
                                    <div className="mb-3">
                                        <Link to="/ticket" className="text-decoration-none text-secondary">
                                            <i className="bi bi-arrow-left me-2"></i>Back to Tickets
                                        </Link>
                                    </div>

                                    <div className="row">
                                        {/* Left Column: Ticket Content */}
                                        <div className="col-md-9" style={{ position: 'relative', zIndex: 2 }}>

                                            {/* Header & First Message Section */}
                                            <div className="bg-white p-4 rounded shadow-sm mb-4 border-top border-4 border-warning position-relative overflow-visible">
                                                {/* Closed Stamp */}
                                                {data.status === 'CLOSED' && (
                                                    <div className="closed-stamp">
                                                        CLOSED
                                                    </div>
                                                )}
                                                <style>{`
                                                    @import url('https://fonts.googleapis.com/css2?family=Black+Ops+One&display=swap');
                                                    
                                                    .message-content img {
                                                        max-width: 100% !important;
                                                        height: auto;
                                                    }
                                                    
                                                    .message-content {
                                                        word-wrap: break-word;
                                                        word-break: break-word;
                                                        overflow-wrap: anywhere;
                                                    }

                                                    .email-content-plaintext {
                                                        white-space: pre-wrap;
                                                        word-wrap: break-word;
                                                        word-break: break-word !important;
                                                        overflow-wrap: anywhere !important;
                                                        hyphens: auto !important;
                                                        text-align: left !important;
                                                        max-width: 100%;
                                                    }

                                                    .email-content-html {
                                                        white-space: normal !important;
                                                        word-wrap: break-word;
                                                        word-break: break-word !important;
                                                        overflow-wrap: anywhere !important;
                                                        hyphens: auto !important;
                                                        text-align: left !important;
                                                        max-width: 100%;
                                                    }

                                                    .email-content-html p {
                                                        margin-bottom: 0 !important;
                                                        white-space: normal !important;
                                                    }

                                                    .message-content * {
                                                        word-wrap: break-word;
                                                        word-break: break-word !important;
                                                        overflow-wrap: anywhere !important;
                                                        hyphens: auto !important;
                                                        text-align: left !important;
                                                        max-width: 100%;
                                                    }

                                                    .message-content pre {
                                                        white-space: pre-wrap !important;
                                                        word-wrap: break-word;
                                                        word-break: break-word !important;
                                                        overflow-wrap: anywhere !important;
                                                    }

                                                    .closed-stamp {
                                                        position: absolute;
                                                        top: 20px;
                                                        right: 50px;
                                                        color: #d9534f;
                                                        font-family: 'Black Ops One', cursive, sans-serif;
                                                        font-size: 4rem;
                                                        font-weight: bold;
                                                        text-transform: uppercase;
                                                        border: 5px solid #d9534f;
                                                        padding: 10px 20px;
                                                        border-radius: 10px;
                                                        transform: rotate(-15deg);
                                                        opacity: 0;
                                                        animation: stamp-animation 0.5s cubic-bezier(0.6, 0.04, 0.98, 0.335) forwards;
                                                        z-index: 10;
                                                        pointer-events: none;
                                                        mix-blend-mode: multiply;
                                                    }

                                                    @keyframes stamp-animation {
                                                        0% {
                                                            opacity: 0;
                                                            transform: rotate(-15deg) scale(3);
                                                        }
                                                        80% {
                                                            opacity: 1;
                                                            transform: rotate(-15deg) scale(0.8);
                                                        }
                                                        100% {
                                                            opacity: 0.8;
                                                            transform: rotate(-15deg) scale(1);
                                                        }
                                                    }
                                                `}</style>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h3 className="mb-1 fw-bold">
                                                        {data.title || "No Title"}
                                                    </h3>
                                                    <div className="text-secondary fs-6 align-items-center">
                                                        #{data?.code || '-'}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-2 mb-4 text-secondary small">
                                                    <span>Created On {data.createdAt ? format(new Date(data.createdAt), "MMM d, yyyy h:mm a") : "-"}</span>
                                                    <span>•</span>
                                                    <span className="badge px-2 py-1 rounded-pill" style={{ backgroundColor: getStatusColor(data?.status) }}>{data?.status?.split("_").join(" ")}</span>
                                                </div>

                                                <div className="d-flex align-items-center mb-4">
                                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', fontSize: '18px' }}>
                                                        {data?.receiveName ? data?.receiveName.charAt(0).toUpperCase() : "U"}
                                                    </div>
                                                    <div>
                                                        {!isEditingSender ? (
                                                            <>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className="fw-bold text-dark">{data?.receiveName || "Unknown User"}</div>
                                                                    <i className="bi bi-pencil-fill text-secondary" style={{ cursor: 'pointer', fontSize: '0.8rem' }} onClick={handleEditSenderClick} title="Edit Sender"></i>
                                                                </div>
                                                                <div className="text-secondary small">{data?.receiveEmail || 'Unknown Email'}</div>
                                                            </>
                                                        ) : (
                                                            <div className="d-flex flex-column gap-2 mt-1" style={{ minWidth: '250px' }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Sender Name"
                                                                    value={senderName}
                                                                    onChange={(e) => setSenderName(e.target.value)}
                                                                />
                                                                <CreatableSelect
                                                                    className="basic-single"
                                                                    classNamePrefix="select"
                                                                    isClearable
                                                                    isSearchable
                                                                    placeholder="Sender Email / Search User..."
                                                                    options={ccOptions}
                                                                    components={{ Option: CcOption }}
                                                                    onChange={handleSenderSelect}
                                                                    onCreateOption={(inputValue) => setSenderEmail(inputValue)}
                                                                    value={senderEmail ? { label: senderEmail, value: senderEmail } : null}
                                                                    styles={{
                                                                        ...customStyles,
                                                                        control: (base) => ({ ...base, minHeight: '30px', fontSize: '12px' })
                                                                    }}
                                                                />
                                                                {senderError && <div className="text-danger small" style={{ fontSize: '0.8rem' }}>{senderError}</div>}
                                                                <div className="d-flex gap-2">
                                                                    <button className="btn btn-sm btn-success" onClick={handleSaveSender}>
                                                                        <i className="bi bi-check"></i> Save
                                                                    </button>
                                                                    <button className="btn btn-sm btn-secondary" onClick={handleCancelEditSender}>
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {firstMessage?.conversationCcs && firstMessage.conversationCcs.length > 0 && (
                                                            <div className="text-secondary small mt-1">
                                                                cc: {firstMessage.conversationCcs.map(cc => cc.email).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    {/* Render First Message Content */}
                                                    <div
                                                        className="text-dark message-content"
                                                        dangerouslySetInnerHTML={{ __html: formatEmailContent(firstMessage?.text || data.description || "No content.") }}
                                                    />
                                                </div>

                                                {/* Attachments for first message if any */}
                                                {firstMessage?.attachments && firstMessage.attachments.length > 0 && (
                                                    <div className="d-flex flex-wrap gap-2 mb-4">
                                                        {firstMessage.attachments.map(att => (
                                                            <div
                                                                key={att.id}
                                                                className="border rounded p-2 d-flex align-items-center gap-2 bg-light small d-inline-flex cursor-pointer"
                                                                onClick={() => handleAttachmentClick(att)}
                                                                style={{ cursor: 'pointer', maxWidth: '100%' }}
                                                            >
                                                                <i className={`bi ${isImage(att.fileName) ? 'bi-file-image' : isVideo(att.fileName) ? 'bi-file-play' : isPdf(att.fileName) ? 'bi-file-pdf' : 'bi-file-earmark-text'}`}></i>
                                                                <span className="d-inline-block text-truncate" style={{ maxWidth: '200px' }}>{att.fileName}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Action Buttons */}
                                                {crmUser.user_level !== 'View' && (
                                                    <>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            {data.status !== 'CLOSED' ? (
                                                                <div className="d-flex gap-2">
                                                                    {!data?.receiveEmail ? (
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id="tooltip-disabled">Make sure received email is available to reply</Tooltip>}
                                                                        >
                                                                            <span className="d-inline-block">
                                                                                <button disabled className={`btn ${editorVisible === 'reply' ? 'btn-success' : 'btn-outline-success'}`} style={{ pointerEvents: 'none' }}>
                                                                                    <i className="bi bi-reply-fill me-1"></i> Reply
                                                                                </button>
                                                                            </span>
                                                                        </OverlayTrigger>
                                                                    ) : (
                                                                        <button className={`btn ${editorVisible === 'reply' ? 'btn-success' : 'btn-outline-success'}`} onClick={handleReplyClick}>
                                                                            <i className="bi bi-reply-fill me-1"></i> Reply
                                                                        </button>
                                                                    )}
                                                                    <button className={`btn ${editorVisible === 'note' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={handleNoteClick}>
                                                                        <i className="bi bi-chat-text-fill me-1"></i> Staff note
                                                                    </button>
                                                                    <button className="btn btn-danger" onClick={handleCloseClick}>
                                                                        <i className="bi bi-lock-fill me-1"></i> Close & lock conversation
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                (() => {
                                                                    const isReopenable = data.closedAt && (new Date() - new Date(data.closedAt)) / (1000 * 60 * 60 * 24) <= 30;
                                                                    return isReopenable ? (
                                                                        <div className="d-flex gap-2">
                                                                            <button className="btn btn-warning text-dark" onClick={handleReopenClick}>
                                                                                <i className="bi bi-arrow-counterclockwise me-1"></i> Re-open Ticket
                                                                            </button>
                                                                        </div>
                                                                    ) : <div />;
                                                                })()
                                                            )}
                                                            <button className="btn btn-link p-0" onClick={handleToggleWatch} disabled={!data} aria-label="Watch">
                                                                <i style={{ fontSize: '20px' }} className={`bi ${data?.isWatch ? 'bi-eye-fill text-warning' : 'bi-eye text-muted'}`}></i>
                                                            </button>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Editor Area */}
                                                {data.status !== 'CLOSED' && editorVisible && (
                                                    <div>
                                                        <div className={`card mt-3 mb-0 ${editorVisible === 'note' ? 'bg-light-blue' : ''}`} style={editorVisible === 'note' ? { backgroundColor: '#e6f0ff', border: '1px solid #b8daff', position: 'relative', zIndex: 2000 } : { border: '1px solid #ddd', position: 'relative', zIndex: 2000 }}>

                                                            {/* Header for Reply */}
                                                            {editorVisible === 'reply' && (
                                                                <div className="card-header bg-white border-bottom p-2">
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                                                                                {data?.receiveName?.charAt(0) || 'U'}
                                                                            </div>
                                                                            <div className="d-flex align-items-center bg-light rounded-2 px-2 py-1 border me-2">
                                                                                <span className="fw-bold small me-1">To</span>
                                                                                <span className="badge bg-warning text-dark me-1">{data?.receiveEmail ? data?.receiveEmail.charAt(0).toUpperCase() : 'Unknown'}</span>
                                                                                <span className="small">{data?.receiveEmail || 'Unknown'}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <button className="btn btn-link text-decoration-none text-secondary p-0 small" onClick={() => setShowCC(!showCC)}>Cc</button>
                                                                            <button type="button" className="btn-close small" aria-label="Close" onClick={handleCancelEditor}></button>
                                                                        </div>
                                                                    </div>

                                                                    {/* CC Input Area */}
                                                                    {showCC && (
                                                                        <div className="d-flex align-items-center border-top pt-2">
                                                                            <span className="text-secondary me-2 small fw-bold">Cc</span>
                                                                            <div className="flex-grow-1">
                                                                                <CreatableSelect
                                                                                    isMulti
                                                                                    options={ccOptions}
                                                                                    value={ccValues}
                                                                                    inputValue={ccInputValue}
                                                                                    onInputChange={(val, meta) => handleCcInputChange(val, meta)}
                                                                                    onKeyDown={handleCcKeyDown}
                                                                                    onChange={setCcValues}
                                                                                    components={{ Option: CcOption, MultiValue: CcMultiValue }}
                                                                                    placeholder="start typing a name or an email"
                                                                                    styles={{
                                                                                        control: (base) => ({ ...base, border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }),
                                                                                        multiValue: (base) => ({ ...base, backgroundColor: '#f0f0f0', borderRadius: '15px', padding: '2px 5px' }),
                                                                                    }}
                                                                                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Header for Staff Note */}
                                                            {editorVisible === 'note' && (
                                                                <div className="card-header border-bottom p-2" style={{ backgroundColor: '#dae8fc' }}>
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px' }}>
                                                                                {JSON.parse(localStorage.getItem('crmUser'))?.full_name?.charAt(0) || 'U'}
                                                                            </div>
                                                                            <span className="fw-bold text-primary me-2">Staff Note</span>
                                                                            <span className="badge bg-white text-primary border border-primary small">Internal</span>
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <button className="btn btn-link text-decoration-none text-primary p-0 small" onClick={() => setShowCC(!showCC)}>Cc</button>
                                                                            <button type="button" className="btn-close small" aria-label="Close" onClick={handleCancelEditor}></button>
                                                                        </div>
                                                                    </div>

                                                                    {/* CC Input Area */}
                                                                    {showCC && (
                                                                        <div className="d-flex align-items-center border-top border-primary-subtle pt-2">
                                                                            <span className="text-primary me-2 small fw-bold">Cc</span>
                                                                            <div className="flex-grow-1">
                                                                                <CreatableSelect
                                                                                    isMulti
                                                                                    options={ccOptions}
                                                                                    value={ccValues}
                                                                                    inputValue={ccInputValue}
                                                                                    onInputChange={(val, meta) => handleCcInputChange(val, meta)}
                                                                                    onKeyDown={handleCcKeyDown}
                                                                                    onChange={setCcValues}
                                                                                    components={{ Option: CcOption, MultiValue: CcMultiValue }}
                                                                                    placeholder="start typing a name or an email"
                                                                                    styles={{
                                                                                        control: (base) => ({ ...base, border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }),
                                                                                        multiValue: (base) => ({ ...base, backgroundColor: '#e2eeff', borderRadius: '15px', padding: '2px 5px' }),
                                                                                    }}
                                                                                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div className="card-body p-0">
                                                                <ReactQuill
                                                                    ref={quillRef}
                                                                    theme="snow"
                                                                    value={editorContent}
                                                                    onChange={(val, delta, source, editor) => {
                                                                        setEditorContent(val);
                                                                        if (replyError) setReplyError("");

                                                                        // [FIX] Sync attachments with editor content on deletion
                                                                        if (source === 'user') {
                                                                            // Extract all current image and video sources from content
                                                                            const tempDiv = document.createElement('div');
                                                                            tempDiv.innerHTML = val;
                                                                            const embeddedMedia = tempDiv.querySelectorAll('img, video');
                                                                            const currentSrcs = Array.from(embeddedMedia).map(el => el.getAttribute('src'));

                                                                            setAttachments(prevAttachments => {
                                                                                return prevAttachments.filter(att => {
                                                                                    // Keep if not image/video (e.g. documents)
                                                                                    const isMedia = att.type?.startsWith('image/') || att.type?.startsWith('video/');
                                                                                    if (!isMedia) return true;

                                                                                    // Keep if URL exists in content
                                                                                    return currentSrcs.includes(att.url);
                                                                                });
                                                                            });
                                                                        }
                                                                    }}
                                                                    modules={modules}
                                                                    formats={formats}
                                                                    placeholder="Write your reply..."
                                                                    style={{
                                                                        backgroundColor: 'white',
                                                                        minHeight: '200px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column'
                                                                    }}
                                                                    className="custom-quill"
                                                                />
                                                                <style>{`
                                                                    .custom-quill .ql-container {
                                                                        flex: 1;
                                                                        min-height: 200px;
                                                                        font-size: 14px;
                                                                        overflow: visible;
                                                                    }
                                                                    .custom-quill .ql-editor {
                                                                        min-height: 200px;
                                                                        overflow: visible;
                                                                    }
                                                                    .custom-quill .ql-tooltip {
                                                                        z-index: 1050;
                                                                    }
                                                                    /* Custom File Button Icon Adjustment */
                                                                    .ql-snow .ql-toolbar button.ql-file {
                                                                        width: 24px;
                                                                        height: 24px;
                                                                        padding: 0;
                                                                        display: inline-flex;
                                                                        align-items: center;
                                                                        justify-content: center;
                                                                        vertical-align: middle;
                                                                    }
                                                                    .ql-snow .ql-toolbar button.ql-file i {
                                                                        line-height: 1;
                                                                        color: #444;
                                                                    }
                                                                    .ql-snow .ql-toolbar button.ql-file:hover i {
                                                                        color: #06c;
                                                                    }
                                                                `}</style>
                                                            </div>
                                                            {/* [FIX] Render Attachments Chips */}
                                                            {attachments.length > 0 && (
                                                                <div className="px-3 pb-3 d-flex flex-wrap gap-2">
                                                                    {attachments.map((att, index) => (
                                                                        <div key={index} className="badge bg-light text-dark border d-flex align-items-center p-2 rounded-pill">
                                                                            <i className={`bi ${att.type?.startsWith('image/') ? 'bi-image' : att.type?.startsWith('video/') ? 'bi-camera-video' : 'bi-file-earmark-text'} me-2`}></i>
                                                                            <span className="me-2" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                {att.name}
                                                                            </span>
                                                                            <i className="bi bi-x-circle-fill text-danger cursor-pointer" onClick={() => removeAttachment(index)} style={{ cursor: 'pointer' }}></i>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {replyError && <div className="px-3 pb-2 text-danger small">{replyError}</div>}
                                                        </div>
                                                        <div className="card-footer bg-transparent d-flex justify-content-end gap-2 pt-3 pb-3">
                                                            <button className="btn btn-outline-secondary" onClick={handleCancelEditor} disabled={isSending}>
                                                                Cancel
                                                            </button>
                                                            <button className={`btn ${editorVisible === 'note' ? 'btn-primary' : 'btn-success'}`} onClick={handlePreSubmit} disabled={isSending}>
                                                                {isSending ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                        Sending...
                                                                    </>
                                                                ) : (
                                                                    editorVisible === 'note' ? 'Save Note' : 'Send Reply'
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* History Divider */}
                                            {history.length > 0 && (
                                                <div className="d-flex align-items-center justify-content-center mb-4">
                                                    <div className="border-bottom border-2 w-100"></div>
                                                </div>
                                            )}

                                            {/* Conversation History List */}
                                            <div className="conversation-history">
                                                {history.map((conv) => (
                                                    <div key={conv.id} className="d-flex mb-4" style={{ marginLeft: conv.staff ? '48px' : '0' }}>
                                                        <div className="flex-shrink-0 me-3 ">
                                                            <div className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${conv.staff ? 'bg-primary' : 'bg-warning'}`} style={{ width: '40px', height: '40px', fontSize: '16px' }}>
                                                                {conv.agentName ? conv.agentName.charAt(0).toUpperCase() : (conv.staff ? "S" : "U")}
                                                            </div>
                                                        </div>
                                                        <div className={`flex-grow-1 border rounded  shadow-sm ${conv.staff ? 'border-primary' : ''}`} style={{ backgroundColor: 'white', minWidth: 0 }}>
                                                            <div className={`card-header rounded-top d-flex justify-content-between align-items-center py-2 px-3 border-bottom ${conv.staff ? '' : 'bg-transparent'}`} style={conv.staff ? { backgroundColor: '#dae8fc' } : {}}>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <span className={`fw-bold me-1 ${conv.staff ? 'text-primary' : 'text-dark'}`}>{conv.agentName || "Unknown"}</span>
                                                                    {conv.staff && <span className="text-primary small me-1">- staff note -</span>}
                                                                    <span className={`${conv.staff ? 'text-primary' : 'text-secondary'} small`}>{!conv.staff && 'commented'} {timeAgo(conv.createdAt)}</span>
                                                                    {conv.conversationCcs && conv.conversationCcs.length > 0 && (
                                                                        <div className={`${conv.staff ? 'text-primary' : 'text-secondary'} small mt-1`}>
                                                                            cc: {conv.conversationCcs.map(cc => cc.email).join(', ')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className={`${conv.staff ? 'text-primary' : 'text-secondary'} small`}>
                                                                    <i className="bi bi-clock me-1"></i>
                                                                    {conv.createdAt ? format(new Date(conv.createdAt), "h:mm a - MMM d, yyyy ") : ""}
                                                                </div>
                                                            </div>
                                                            <div className="card-body p-3">
                                                                <CollapsibleMessage content={conv.text} />
                                                                {conv.attachments && conv.attachments.length > 0 && (
                                                                    <div className="mt-3 pt-3 border-top">
                                                                        {conv.attachments.map(att => (
                                                                            <div
                                                                                key={att.id}
                                                                                className="border rounded p-2 d-flex align-items-center gap-2 bg-light small d-inline-flex me-2 cursor-pointer img-fluid"
                                                                                onClick={() => handleAttachmentClick(att)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            >
                                                                                <i className={`bi ${isImage(att.fileName) ? 'bi-file-image' : isVideo(att.fileName) ? 'bi-file-play' : isPdf(att.fileName) ? 'bi-file-pdf' : 'bi-file-earmark-text'}`}></i>
                                                                                <span>{att.fileName}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Sentinel for IntersectionObserver */}
                                                <div ref={observerTarget} style={{ height: '10px' }}></div>

                                                {isFetchingMore && (
                                                    <div className="d-flex justify-content-center align-items-center py-4">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right Column: Sidebar */}
                                        {crmUser.user_level !== 'View' && (
                                            <>
                                                <div className="col-md-3" style={{ position: 'sticky', top: 0, maxHeight: '100vh', overflowY: 'auto', alignSelf: 'flex-start' }}>
                                                    <div className="bg-white p-3 rounded shadow-sm">
                                                        <div className="mb-3">
                                                            <label className="form-label small text-uppercase text-secondary">Status</label>
                                                            <Select
                                                                options={statusOptions}
                                                                value={selectedStatus}
                                                                onChange={(opt) => {
                                                                    setSelectedStatus(opt);
                                                                    updateTicketField('status', opt.value);
                                                                }}
                                                                styles={customStyles}
                                                                components={{ Option: StatusOption, SingleValue: StatusSingleValue }}
                                                            />
                                                        </div>

                                                        <div className="mb-3">
                                                            <label className="form-label small text-uppercase text-secondary">Priority</label>
                                                            <Select
                                                                options={priorityOptions}
                                                                value={selectedPriority}
                                                                onChange={(opt) => {
                                                                    setSelectedPriority(opt);
                                                                    updateTicketField('priority', opt.value);
                                                                }}
                                                                styles={customStyles}
                                                                components={{ Option: PriorityOption, SingleValue: PrioritySingleValue }}
                                                            />
                                                        </div>

                                                        <div className="mb-3">
                                                            <label className="form-label small text-uppercase text-secondary">Queue</label>
                                                            <Select
                                                                options={queueOptions}
                                                                value={selectedQueue}
                                                                onChange={(opt) => {
                                                                    setSelectedQueue(opt);
                                                                    updateTicketField('queueId', opt.value);
                                                                }}
                                                                styles={customStyles}
                                                                placeholder="Select Queue..."
                                                            />
                                                        </div>

                                                        <div className="mb-3">
                                                            <label className="form-label small text-uppercase text-secondary">Agent</label>
                                                            <Select
                                                                options={agentOptions}
                                                                value={selectedAgent}
                                                                onChange={(opt) => {
                                                                    setSelectedAgent(opt);
                                                                    updateTicketFields({
                                                                        userEmail: opt.email,
                                                                        userName: opt.label,
                                                                        status: selectedStatus?.value || data.status
                                                                    });
                                                                }}
                                                                styles={customStyles}
                                                                placeholder="Unassigned"
                                                            />
                                                        </div>

                                                        <div className="mb-3">
                                                            <label className="form-label small text-uppercase text-secondary">Tags</label>
                                                            <Select
                                                                isMulti
                                                                options={tagOptions}
                                                                value={selectedTags}
                                                                onChange={(opts) => {
                                                                    setSelectedTags(opts);
                                                                    const tagIds = opts ? opts.map(o => o.value) : [];
                                                                    updateTicketField('tags', tagIds);
                                                                }}
                                                                styles={customStyles}
                                                                placeholder="Select tags..."
                                                            />
                                                        </div>


                                                        <div className="mb-3">
                                                            <label className="form-label small text-uppercase text-secondary">Resolution</label>
                                                            <Select
                                                                options={resolutionOptions}
                                                                value={selectedResolution}
                                                                onChange={(opt) => {
                                                                    setSelectedResolution(opt);
                                                                    updateTicketField('resolution', opt.value);
                                                                }}
                                                                styles={customStyles}
                                                                placeholder="Select Resolution"
                                                            />
                                                        </div>

                                                        <div className="mb-3">
                                                            <label className="form-label small text-uppercase text-secondary">Type</label>
                                                            <Select
                                                                options={typeOptions}
                                                                value={selectedType}
                                                                onChange={(opt) => {
                                                                    setSelectedType(opt);
                                                                    updateTicketField('type', opt.value);
                                                                }}
                                                                styles={customStyles}
                                                                placeholder="Select Type"
                                                            />
                                                        </div>

                                                        <hr />

                                                        <div className="mb-2">
                                                            <div className="small text-secondary text-uppercase">First assigned on</div>
                                                            <div className="small fw-bold">{data.createdAt ? format(new Date(data.createdAt), "MMM d, yyyy h:mm a") : "-"}</div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <div className="small text-secondary text-uppercase">Updated on</div>
                                                            <div className="small fw-bold">{data.updatedAt ? format(new Date(data.updatedAt), "MMM d, yyyy h:mm a") : "-"}</div>
                                                        </div>

                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 text-center">Ticket not found</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal for Submit */}
            <Modal show={showSubmitConfirmModal} onHide={() => setShowSubmitConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">
                        {editorVisible === 'note' ? 'Confirm Staff Note' : 'Confirm Reply'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-3">
                        <i className={`bi ${editorVisible === 'note' ? 'bi-chat-text-fill text-primary' : 'bi-reply-fill text-success'} display-1`}></i>
                    </div>
                    <p className="text-center fs-5">
                        Are you sure you want to {editorVisible === 'note' ? 'add this internal note' : 'send this reply'}?
                    </p>
                    {editorVisible === 'note' && (
                        <div className="alert alert-primary d-flex align-items-center" role="alert">
                            <i className="bi bi-info-circle-fill me-2"></i>
                            <div>
                                This note is <strong>internal</strong> and will not be visible to the customer.
                            </div>
                        </div>
                    )}
                    {editorVisible === 'reply' && (
                        <div className="alert alert-warning d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                This reply will be sent <strong>publicly</strong> to the customer.
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSubmitConfirmModal(false)} disabled={isSending}>
                        Cancel
                    </Button>
                    <Button
                        variant={editorVisible === 'note' ? 'primary' : 'success'}
                        onClick={confirmSubmit}
                        disabled={isSending}
                    >
                        {isSending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Sending...
                            </>
                        ) : (
                            'Yes, Confirm'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Close Confirmation Modal */}
            <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Close Ticket</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to close and lock this conversation? You can re-open this ticket within 30 days.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCloseModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmClose}>
                        Close & Lock
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reopen Confirmation Modal */}
            <Modal show={showReopenModal} onHide={() => setShowReopenModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Re-open Ticket</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to re-open this ticket? It will be set to In Progress.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReopenModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={confirmReopen}>
                        Re-open
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Image Preview Modal */}
            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Attachment Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {previewImage && (
                        <img src={previewImage} alt="Preview" className="img-fluid" style={{ maxHeight: '90vh', maxWidth: '100%' }} />
                    )}
                    {previewVideo && (
                        <video controls className="img-fluid" style={{ maxHeight: '90vh', maxWidth: '100%' }}>
                            <source src={previewVideo} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    )}
                    {previewPdf && (
                        <iframe src={previewPdf} className="w-100" style={{ height: '80vh' }} title="PDF Preview"></iframe>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};
export default TicketDetail;




